import type { AssistantRenderSettings } from '@/types/assistant';
import type { RenderEffort } from '@/types/render';
import type { EnvironmentSetting } from '@/shared/constants';
import type { RenderFormData } from '../model/slices/internals';
import { initialFormData } from '../model/slices/internals';

/**
 * The AI architect edits the unsaved settings form through a patch the client
 * applies (docs/assistant/ADR-001 §3, path C). The rules are shared with the
 * API and with Apple and Android through the fixtures in
 * `apps/api/tests/Fixtures/assistant/render-settings-patches` — every client
 * runs every fixture through its applier, so the three cannot drift:
 *
 *   1. A field absent from the patch keeps its value.
 *   2. A field present replaces the value. Lists are replaced whole.
 *   3. `null` resets the field to Auto.
 *   4. `prompts` is merged key by key.
 *   5. `mode` switches the form's tab first; the other fields land on that tab.
 *
 * A field this build does not know is not applied; it is reported back in the
 * next message's `context.skipped_fields`, and the server decides what to tell
 * the user.
 */

/** `RenderSettingsRules::PATCH_FIELDS` on the API, in its order. */
export const RENDER_SETTINGS_PATCH_FIELDS = [
  'mode',
  'style_ids',
  'material_ids',
  'colors',
  'brand_selections',
  'style_project_reference_ids',
  'material_project_reference_ids',
  'color_project_reference_ids',
  'output_project_reference_ids',
  'render_effort',
  'prompts',
  'environment_setting',
] as const satisfies ReadonlyArray<keyof AssistantRenderSettings>;

type PatchField = (typeof RENDER_SETTINGS_PATCH_FIELDS)[number];

const PROMPT_KEYS = ['prompt_concept', 'prompt_angle'] as const;

const isPatchField = (key: string): key is PatchField =>
  (RENDER_SETTINGS_PATCH_FIELDS as readonly string[]).includes(key);

export interface RenderSettingsPatchResult {
  settings: AssistantRenderSettings;
  /** Patch keys this build does not know, for `context.skipped_fields`. */
  skipped: string[];
}

/** Apply a patch to a form in the patch schema (pure; the fixtures run this). */
export function applyRenderSettingsPatch(
  settings: AssistantRenderSettings,
  patch: Record<string, unknown>
): RenderSettingsPatchResult {
  const next: AssistantRenderSettings = {
    ...settings,
    prompts: { ...settings.prompts },
  };
  const skipped: string[] = [];

  for (const [key, value] of Object.entries(patch)) {
    if (!isPatchField(key)) {
      skipped.push(key);
      continue;
    }
    if (key === 'prompts') {
      const prompts = (value ?? {}) as Record<string, unknown>;
      for (const promptKey of Object.keys(prompts)) {
        if ((PROMPT_KEYS as readonly string[]).includes(promptKey)) {
          next.prompts[promptKey as (typeof PROMPT_KEYS)[number]] =
            (prompts[promptKey] as string | null) ?? null;
        } else {
          skipped.push(`prompts.${promptKey}`);
        }
      }
      continue;
    }
    (next as unknown as Record<string, unknown>)[key] = Array.isArray(value)
      ? [...value]
      : value;
  }

  return { settings: next, skipped };
}

/** The form as `context.render_settings` (what the model sees as selected). */
export function renderSettingsSnapshot(
  form: RenderFormData
): AssistantRenderSettings {
  return {
    mode: form.mode,
    style_ids: form.style_ids,
    material_ids: form.material_ids,
    colors: form.colors,
    brand_selections: form.brand_selections,
    style_project_reference_ids: form.style_project_reference_ids,
    material_project_reference_ids: form.material_project_reference_ids,
    color_project_reference_ids: form.color_project_reference_ids,
    output_project_reference_ids: form.output_project_reference_ids,
    render_effort: form.render_effort ?? null,
    prompts: {
      prompt_concept: form.prompts?.prompt_concept ?? null,
      prompt_angle: form.prompts?.prompt_angle ?? null,
    },
    environment_setting: form.environment_setting,
  };
}

/**
 * The store update for the given patch fields of a patched snapshot.
 *
 * List fields keep `null` as the form's own Auto. Two form fields cannot hold
 * `null` and take the value a fresh draft starts with, which is what Auto means
 * for them: effort (`initialFormData.render_effort`) and environment
 * (`NO_CHANGE`). A `null` prompt is the empty text field. `mode` is not a
 * form write: the tab is switched through the store's own mode toggle.
 */
export function renderFormPartial(
  settings: AssistantRenderSettings,
  fields: Iterable<string>,
  currentPrompts: RenderFormData['prompts']
): Partial<RenderFormData> {
  const partial: Partial<RenderFormData> = {};
  for (const field of fields) {
    switch (field) {
      case 'render_effort':
        partial.render_effort =
          (settings.render_effort as RenderEffort | null) ??
          initialFormData.render_effort;
        break;
      case 'environment_setting':
        partial.environment_setting =
          (settings.environment_setting as EnvironmentSetting | null) ??
          initialFormData.environment_setting;
        break;
      case 'mode':
        break;
      case 'prompts':
        partial.prompts = {
          ...currentPrompts,
          prompt_concept: settings.prompts.prompt_concept ?? '',
          prompt_angle: settings.prompts.prompt_angle ?? '',
        };
        break;
      default:
        if (isPatchField(field)) {
          (partial as Record<string, unknown>)[field] = settings[field];
        }
    }
  }
  return partial;
}
